import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_HOTEL,
} from 'constants/offer'
import config from 'constants/config'
import { OverlayView, OverlayViewF } from '@react-google-maps/api'
import PropertyMarker from 'components/Common/PropertyMap/PropertyMarkerV2'
import PropertyPriceMarker from './PropertyPriceMarker'
import Clickable from 'components/Common/Clickable'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import {
  getOfferId,
} from 'components/Search/SearchResultsMap/utils'
import { autoPlacement, autoUpdate, offset, shift, useFloating } from '@floating-ui/react-dom'
import SearchResultsMapOfferView from 'components/Search/SearchResultsMap/SearchResultsMapOfferView'

const MAX_PIN_LIMIT = config.search.mapView.pinLimit
const MAP_TILE_EDGE_GAP = 16
const MAP_TILE_TOP_OFFSET = 64
const MAP_TILE_SIDE_OFFSET = 28

interface Props {
  onMarkerClick: (
    marker: App.SearchMapMarker,
    pinType: string,
    e?: Event | React.MouseEvent<Element | MouseEvent>,
  ) => void
  isActive: boolean
  isHovered: boolean
  isVisited: boolean
  filters: App.OfferListFilters
  onMarkerLoad?: (
    markerInstance: google.maps.Marker,
    markerData: App.SearchMapMarker,
  ) => void
  onMarkerUnmount?: (markerInstance: google.maps.Marker) => void
  onDeselectOffers?: () => void
  marker: App.SearchMapMarker
  index: number
  handleProductClick?: (
    offer:
      | App.Offer
      | App.OfferSummary
      | App.BedbankOffer
      | App.BedbankOfferSummary,
    position: number,
  ) => void
  onHover?: (offerId: string | undefined, type: string | undefined) => void
  soldOut: boolean
  isSaved?: boolean
  map?: google.maps.Map | null
  animateMarker?: boolean
  showOfferViewWhenActive?: boolean
  priceLabel?: string
  isLuxPlusPrice?: boolean
}

function HotelMapMarker(props: Props) {
  const {
    marker,
    index,
    onMarkerClick,
    isActive,
    isVisited,
    filters,
    onDeselectOffers,
    handleProductClick,
    isHovered,
    onHover,
    soldOut,
    isSaved,
    map,
    animateMarker = false,
    showOfferViewWhenActive = true,
    priceLabel,
    isLuxPlusPrice = false,
  } = props

  const calculateZIndex = useCallback(
    (marker: App.SearchMapMarker, index: number) => {
      if (isActive) {
        return MAX_PIN_LIMIT + 1
      }
      if (isHovered) {
        return MAX_PIN_LIMIT
      }
      if (soldOut) {
        return -1
      }
      if (index >= MAX_PIN_LIMIT) {
        return 0
      }
      const offerType = marker.offers[0].type
      if (
        offerType === OFFER_TYPE_HOTEL ||
        offerType === OFFER_TYPE_ALWAYS_ON ||
        offerType === OFFER_TYPE_BED_BANK
      ) {
        return MAX_PIN_LIMIT - index
      }
      return 0
    },
    [isActive, isHovered, soldOut],
  )

  const handleClick = useCallback(
    (e: Event | React.MouseEvent<Element | MouseEvent>) => {
      onMarkerClick(marker, index < MAX_PIN_LIMIT ? 'pricePin' : 'pill', e)
      e.stopPropagation()
    },
    [index, marker, onMarkerClick],
  )

  const handleMouseEnter = useCallback(() => {
    if (onHover) {
      onHover(marker.id, marker.offers[0].type)
    }
  }, [marker.id, marker.offers, onHover])

  const handleMouseLeave = useCallback(() => {
    if (onHover) {
      onHover(undefined, undefined)
    }
  }, [onHover])

  const offerIds = useMemo(() => marker.offers.map(getOfferId), [marker.offers])

  const zIndex = useMemo(() => calculateZIndex(marker, index), [calculateZIndex, index, marker])

  const { refs, floatingStyles, update } = useFloating({
    middleware: [
      offset(({ placement }) => {
        // Reference point is the marker anchor (pin tip), so top placement
        // needs extra offset to clear the visible pin body.
        if (placement.includes('top')) return MAP_TILE_TOP_OFFSET
        if (placement.includes('bottom')) return MAP_TILE_EDGE_GAP
        return MAP_TILE_SIDE_OFFSET
      }),
      shift({ padding: MAP_TILE_EDGE_GAP }),
      autoPlacement({ padding: MAP_TILE_EDGE_GAP }),
    ],
    open: isActive,
    whileElementsMounted: autoUpdate,
  })

  const [listener, setListener] = useState<google.maps.MapsEventListener | undefined>(undefined)

  useEffect(() => {
    if (!map) return

    if (!isActive) {
      if (listener) {
        google.maps.event.removeListener(listener)
        setListener(undefined)
      }
      return
    }

    // Only add listener if one doesn't already exist
    if (!listener) {
      const newListener = map.addListener('idle', update)
      setListener(newListener)
    }

    return () => {
      if (listener) {
        google.maps.event.removeListener(listener)
        setListener(undefined)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, map])

  const position = useMemo<google.maps.LatLngLiteral>(() => {
    return {
      lat: marker.latitude ?? 0,
      lng: marker.longitude ?? 0,
    }
  }, [marker.longitude, marker.latitude])

  return (
    <OverlayViewF
      key={marker.id}
      position={position}
      zIndex={zIndex}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <Clickable
        ref={refs.setReference}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {priceLabel && <PropertyPriceMarker
          isActive={isActive}
          isHovered={isHovered}
          isVisited={isVisited}
          isLuxPlusPrice={isLuxPlusPrice}
          label={priceLabel}
        />}
        {!priceLabel && <PropertyMarker
          key={marker.id}
          marker={marker}
          active={isActive}
          isHovered={isHovered}
          visited={isVisited}
          soldOut={soldOut}
          isSaved={isSaved}
          animateMarker={animateMarker}
        />}
      </Clickable>
      {showOfferViewWhenActive && (
        <CSSBreakpoint min="tablet">
          <div ref={refs.setFloating} style={floatingStyles}>
            {isActive && (
              <SearchResultsMapOfferView
                key={marker.id}
                offerIds={offerIds}
                filters={filters}
                onProductClick={handleProductClick}
                onDeselectOffers={onDeselectOffers}
              />
            )}
          </div>
        </CSSBreakpoint>
      )}
    </OverlayViewF>
  )
}

export default React.memo(HotelMapMarker)
